import { defineStore } from 'pinia'
import stripCachedDocuments from '@/utilities/cachedDocuments'
import { ref, computed, ComputedRef } from 'vue'
import {
  Answer,
  errorMessage,
  SchemaType,
  SupplementalFormType,
} from '@verifiedit/form-generator'

export const usePermitFormStore = defineStore(
  'permitForm',
  () => {
    /** state */
    const startPermit = ref<(SchemaType & errorMessage)[]>([])
    const endPermit = ref<(SchemaType & errorMessage)[]>([])
    const answers = ref<{ [key: string]: Answer }>({})
    const endPermitId = ref<number | null>(null)
    const startPermitId = ref<number | null>(null)
    const startPermitSupplementals = ref<SupplementalFormType[]>([])

    /** getters */
    const getPermitAnswers: ComputedRef<Map<string, Answer>> = computed(
      () => new Map(Object.entries(answers.value)),
    )

    const hasEndPermit: ComputedRef<boolean> = computed(
      () => endPermit.value.length !== 0,
    )

    const hasSubmittedStartPermit: ComputedRef<boolean> = computed(
      () => Object.keys(answers.value).length !== 0,
    )

    /** actions */
    function setStartPermitSupplementals(value: SupplementalFormType[]) {
      startPermitSupplementals.value = value
    }

    function setPermitAnswers(value: { [key: string]: Answer }): void {
      answers.value = value
    }

    function setStartPermitId(value: number): void {
      startPermitId.value = value
    }

    function setEndPermitId(value: number): void {
      endPermitId.value = value
    }

    function setStartPermit(value: SchemaType[]): void {
      value.forEach((component) => {
        component.errorMessage = ''
      })
      startPermit.value = value as (SchemaType & errorMessage)[]
    }

    function setEndPermit(value: SchemaType[]): void {
      value.forEach((component) => {
        component.errorMessage = ''
      })
      endPermit.value = value as (SchemaType & errorMessage)[]
    }
    function resetPermitForm(): void {
      answers.value = {}
      endPermit.value = []
      startPermit.value = []
      endPermitId.value = null
      startPermitId.value = null
      startPermitSupplementals.value = []
    }

    return {
      answers,
      endPermit,
      endPermitId,
      getPermitAnswers,
      hasEndPermit,
      hasSubmittedStartPermit,
      resetPermitForm,
      setEndPermit,
      setEndPermitId,
      setPermitAnswers,
      setStartPermit,
      setStartPermitId,
      setStartPermitSupplementals,
      startPermit,
      startPermitId,
      startPermitSupplementals,
    }
  },
  {
    persist: {
      afterHydrate: (context) => refreshCachedPermitForms(context.store.$state),
    },
  },
)

/**
 * What is persisted here is the pristine backend template, and the documents a component holds
 * are attached to a local copy of it on every load — so anything document-shaped that has made
 * it into the cache is stale, and a `blob:` url in it points at nothing this session can read.
 * Dropped on hydrate, so the form refetches rather than renders it (see `stripCachedDocuments`).
 */
export function refreshCachedPermitForms(state: {
  endPermit?: unknown
  startPermit?: unknown
  startPermitSupplementals?: unknown
}): void {
  stripCachedDocuments(state.endPermit)
  stripCachedDocuments(state.startPermit)
  stripCachedDocuments(state.startPermitSupplementals)
}
